import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Switch } from 'rizzui';
import { SpecieService } from '../../../../services/specie/specieService';
import useFetch from '../../../../hooks/useFetch';
import FormGroup from '../../../../layout/shared/form-group';
import AsyncImg from '../../../../components/extras/AsyncImg';
import PlaceholderImage from '../../../../components/extras/PlaceholderImage';
import SpeciesProfileLayout from '../SpeciesProfileLayout';
import Viewer from 'react-viewer';
import { DocumentsService } from '../../../../services/document/documentsService';
import { riskLevelOptions, speciesGroupSelector } from '../../../../utils/SelectorMapping';
import CustomSelect from '../../../../components/forms/CustomSelect';


interface SpeciesInfoProps { }

const SpeciesInfo: React.FC<SpeciesInfoProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const service = new SpecieService();
    const documentsService = new DocumentsService();

    // Estados para el visor de imágenes
    const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
    const [viewerImages, setViewerImages] = useState<any[]>([]);

    const [data] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getSpecieById(id);
        return response.getResponseData();
    }, [id]));

    // Función para abrir el visor de imágenes
    const handleOpenViewer = async () => {
        if (data?.document?.id) {
            try {
                const response = await documentsService.renderDocument(data.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + data.document.extension });
                const imageUrl = URL.createObjectURL(file);
                
                setViewerImages([{
                    src: imageUrl,
                    alt: data.scientificName || 'Imagen de especie'
                }]);
                setIsViewerOpen(true);
            } catch (error) {
                console.error('Error al cargar la imagen:', error);
            }
        }
    };

    // Función para cerrar el visor
    const handleCloseViewer = () => {
        setIsViewerOpen(false);
        setViewerImages([]);
    };
 

    return (
        <SpeciesProfileLayout>
            <div className="@container">
                <div className="grid divide-y-2 divide-dashed divide-gray-200 gap-6">
                    <FormGroup
                        title="Datos de la Especie"
                        description='Información general de la especie registrada'
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <div className='md:col-span-2 md:grid md:grid-cols-2 gap-4'>

                            <div className="col-span-1">
                            <label className="rizzui-input-label block text-sm mb-1.5 font-medium text-muted-foreground">Nombre Científico</label>
                            <div className="rizzui-input-container flex items-center peer w-full transition duration-200 [&.is-focus]:ring-[0.8px] ring-[0.6px] [&.is-hover]:border-primary [&.is-focus]:border-primary [&.is-focus]:ring-primary [&_input::placeholder]:opacity-60 px-3.5 py-2 text-sm h-10 rounded-md border border-muted ring-muted bg-transparent !bg-muted/70 backdrop-blur cursor-not-allowed !border-muted italic">
                                {data?.scientificName ?? ''}
                            </div>
                            </div>

                            <Input
                                disabled
                                label="Nombre Común"
                                placeholder="Common Name"
                                value={data?.commonName ?? ''}
                                className="col-span-1"
                            />

                            <Input
                                disabled
                                label="Grupo"
                                placeholder="Grupo"
                                value={
                                    speciesGroupSelector.find((item) => item.value === data?.family)?.label ?? ''
                                }
                                className="col-span-2"
                            />

                            <Input
                                disabled
                                label="Identificador de Especie"
                                placeholder="specieIdentifier "
                                value={data?.specieIdentifier ?? ''}
                                className=""
                            />

                            <Input
                                disabled
                                label="Acrónimo"
                                placeholder="Acronym"
                                value={data?.acronym ?? ''}
                                className=""
                            />
                            <Input
                                disabled
                                label="SEO"
                                placeholder="SEO"
                                value={data?.infoPage ?? ''}
                                className="col-span-2"
                            />

                        </div>

                        <div></div>

                        <div className="relative">
                            {data?.document?.id ? (
                                <div 
                                    className="cursor-pointer transition-transform hover:scale-105 hover:shadow-lg rounded relative overflow-hidden"
                                    onClick={handleOpenViewer}
                                    title="Haz clic para ampliar la imagen"
                                    style={{ width: '220px', height: '220px' }}
                                >
                                    <AsyncImg 
                                        id={data?.document?.id} 
                                        onClick={handleOpenViewer}
                                        className="mx-auto d-block img-fluid rounded w-[220px] h-[220px] object-cover" 
                                    />
                                    {/* Indicador visual de que es clickeable */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 rounded flex items-center justify-center pointer-events-none">
                                        <div className="opacity-0 hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                                            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <PlaceholderImage width={200} height={200} className='mx-auto d-block img-fluid rounded' />
                            )}
                            
                            {/* Componente Viewer para mostrar la imagen ampliada */}
                            {data?.document?.id && (
                                <Viewer
                                    visible={isViewerOpen}
                                    onClose={handleCloseViewer}
                                    images={viewerImages}
                                    noNavbar
                                    scalable={false}
                                    rotatable={false}
                                    changeable={false}
                                    zoomable={true}
                                    downloadable={false}
                                    noImgDetails
                                    noToolbar={false}
                                    zoomSpeed={1.5}
                                />
                            )}
                        </div>
                    </FormGroup>

                    <FormGroup
                        title="Datos Adicionales"
                        description="Información relevante para la gestión de la especie"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Switch
                            id="censusAvailable"
                            label="Disponible para Censo"
                            checked={data?.censusAvailable ?? false}
                            className="flex-grow col-span-12"
                        />

                        <Switch
                            id="OFCAvailable"
                            label="Disponible para OFC"
                            checked={data?.ofcAvailable ?? false}
                            className="flex-grow col-span-12"
                        />
                        <Switch
                            id="issuesAvailable"
                            label="Disponible para Incidencias"
                            checked={data?.issueAvailable ?? false}
                            className="flex-grow col-span-12"
                        />
                        <Switch
                            id="carrionAvailable"
                            label="Carroña disponible"
                            checked={data?.carrionAvailable ?? false}
                            className="flex-grow col-span-12"
                        />
                        <Switch
                            id="catAndalucia"
                            label="Especie amenazada a nivel de Andalucía"
                            checked={data?.catAndalucia ?? false}
                            className="flex-grow col-span-12"
                        />

                        {data?.catAndalucia && (
                            <CustomSelect
                                isSearchable
                                id={'riskLevelAndalucia'}
                                label="Nivel de riesgo andaluz"
                                options={riskLevelOptions}
                                value={
                                    riskLevelOptions.filter((option: any) => option.value === data?.riskLevelAndalucia)
                                }
                                required
                                className="mb-4"
                                isDisabled={true}
                            />
                        )}

                        <Switch
                            id="catNacional"
                            label="Especie amenazada a nivel de Nacional"
                            checked={data?.catAndalucia ?? false}
                            className="flex-grow col-span-12"
                        />

                        {data?.catNacional && (
                            <CustomSelect
                                isSearchable
                                id={'riskLevelNacional'}
                                label="Nivel de riesgo Nacional"
                                options={riskLevelOptions}
                                value={
                                    riskLevelOptions.filter((option: any) => option.value === data?.riskLevelNacional)
                                }
                                required
                                className="mb-4"
                                isDisabled={true}
                            />
                        )}




                        
                        
                    </FormGroup>
                </div>
            </div>
        </SpeciesProfileLayout>
    );
};

export default SpeciesInfo;